import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import ReactTable from '../../components/ReactTable';
import Loader from '../../components/Loader';
import { paths } from '../../routes/helpers';
import { fetchKeywordCategories, deleteKeywordCategory, duplicateKeywordCategory, setSorting, bulkDeleteKeywordCategories, bulkUpdateKeywordCategoryStatus } from './keywordCategorySlice';

export default function KeywordCategoryList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, loading, error, total, pages, sortBy, sortOrder } = useSelector((state) => state.keywordCategory);

  const [currentPage, setCurrentPage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const searchTimeoutRef = useRef(null);

  // Debounce search: update searchTerm after user stops typing
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1); // Reset to first page when searching
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput]);

  // Fetch data when searchTerm, page, sortBy, or sortOrder changes
  useEffect(() => {
    dispatch(fetchKeywordCategories({ page: currentPage, limit: currentLimit, search: searchTerm, sortBy, sortOrder }));
  }, [dispatch, currentPage, currentLimit, searchTerm, sortBy, sortOrder]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setCurrentLimit(newLimit);
    setCurrentPage(1);
    dispatch(fetchKeywordCategories({ page: 1, limit: newLimit, search: searchTerm, sortBy, sortOrder }));
  };

  const handleSearch = (value) => {
    setSearchInput(value);
  };

  const handleDelete = async (id) => {
    const keywordCategory = items.find(item => item._id === id);
    const categoryName = keywordCategory?.name || 'this keyword category';
    
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${categoryName}". This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deleteKeywordCategory(id)).unwrap();
        Swal.fire({
          title: 'Deleted!',
          text: 'Keyword category has been deleted successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        Swal.fire({
          title: 'Error!',
          text: error || 'Failed to delete keyword category.',
          icon: 'error'
        });
      }
    }
  };

  const handleEdit = (id) => navigate(paths.private.keywordCategory.edit(id));
  const handleCreate = () => navigate(paths.private.keywordCategory.create());

  const handleDuplicate = async (id) => {
    const keywordCategory = items.find(item => item._id === id);
    const categoryName = keywordCategory?.name || 'this keyword category';
    
    const result = await Swal.fire({
      title: 'Duplicate Keyword Category',
      text: `Are you sure you want to duplicate "${categoryName}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, duplicate it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await dispatch(duplicateKeywordCategory(id)).unwrap();
        Swal.fire({
          title: 'Duplicated!',
          text: 'Keyword category has been duplicated successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
        // Refresh the list to show the new duplicate
        dispatch(fetchKeywordCategories({ page: currentPage, limit: currentLimit, search: searchTerm, sortBy, sortOrder }));
      } catch (error) {
        Swal.fire({
          title: 'Error!',
          text: 'Failed to duplicate keyword category.',
          icon: 'error'
        });
      }
    }
  };

  const handleBulkDelete = async (ids, clearSelection) => {
    const result = await Swal.fire({
      title: `Delete ${ids.length} keyword categor${ids.length > 1 ? 'ies' : 'y'}?`,
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      await dispatch(bulkDeleteKeywordCategories(ids)).unwrap();
      Swal.fire({ title: 'Deleted!', text: `${ids.length} keyword categor${ids.length > 1 ? 'ies' : 'y'} deleted.`, icon: 'success', timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ title: 'Some deletions failed', text: err || 'One or more items are still in use elsewhere and were not deleted.', icon: 'error' });
    } finally {
      clearSelection();
    }
  };

  const handleBulkStatusChange = (status) => async (ids, clearSelection) => {
    const activating = status === 1;
    const result = await Swal.fire({
      title: `${activating ? 'Activate' : 'Deactivate'} ${ids.length} keyword categor${ids.length > 1 ? 'ies' : 'y'}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: `Yes, ${activating ? 'activate' : 'deactivate'}`,
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    dispatch(bulkUpdateKeywordCategoryStatus({ ids, status })).then(() => {
      Swal.fire({ title: 'Updated!', text: `${ids.length} keyword categor${ids.length > 1 ? 'ies' : 'y'} ${activating ? 'activated' : 'deactivated'}.`, icon: 'success', timer: 1500, showConfirmButton: false });
      clearSelection();
    });
  };

  const handleSort = (columnKey, newSortOrder) => {
    dispatch(setSorting({ sortBy: columnKey, sortOrder: newSortOrder }));
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };

  const headers = [
    { label: 'Name', key: 'name' },
    { label: 'Keywords', key: 'keywords' },
    { label: 'Status', key: 'status' },
    { label: 'Created At', key: 'createdAt' },
    { label: 'Updated At', key: 'updatedAt' },
  ];

  const formatTableData = (rows) => rows.map((r) => ({
    _id: r._id,
    name: r.name,
    keywords: r.keywords ? r.keywords.join(', ') : '-',
    status: r.status ? 'Active' : 'Inactive',
    createdAt: r.createdAt ? new Date(r.createdAt).toLocaleString() : '-',
    updatedAt: r.updatedAt ? new Date(r.updatedAt).toLocaleString() : '-',
  }));

  const actions = [
    { label: 'Edit', onClick: handleEdit, color: 'blue' },
    { label: 'Duplicate', onClick: handleDuplicate, color: 'green' },
    { label: 'Delete', onClick: handleDelete, color: 'red' },
  ];

  if (error) return <div className="p-4 text-red-500">Error: {error}</div>;

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-bold">Keyword Categories</h1>
        <button onClick={handleCreate} className="bg-green-500 text-white px-4 py-2 rounded">Create New</button>
      </div>

      <ReactTable
        headers={headers}
        data={formatTableData(items)}
        actions={actions}
        onSearch={handleSearch}
        searchValue={searchInput}
        searchDelay={1000}
        searchKey="name"
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        loading={loading}
        pagination={items.length > 0 ? { page: currentPage, limit: currentLimit, total, pages } : null}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        enableRowSelection={true}
        bulkActions={[
          { label: 'Activate Selected', color: 'green', onClick: handleBulkStatusChange(1) },
          { label: 'Deactivate Selected', color: 'gray', onClick: handleBulkStatusChange(0) },
          { label: 'Delete Selected', color: 'red', onClick: handleBulkDelete },
        ]}
      />

      {items.length === 0 && !loading && (
        <div className="text-center py-8">
          <div className="text-gray-500 text-lg mb-2">No keyword categories found</div>
          <div className="text-gray-400 text-sm">
            Create your first keyword category to get started
          </div>
        </div>
      )}
    </div>
  );
}


